import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LANGUAGE_DOMAINS, SUPPORTED_LANGUAGES, translations } from "../data/i18n";

const STORAGE_KEY = "xsiadron-language";
const DEFAULT_LANGUAGE = "en";

const I18nContext = createContext({
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    t: (key) => key,
    translateTag: (tag) => tag,
    translateProject: (project) => project,
});

function getValueByPath(source, path) {
    return path.split(".").reduce((value, part) => {
        if (value && Object.prototype.hasOwnProperty.call(value, part)) {
            return value[part];
        }

        return undefined;
    }, source);
}

function interpolate(value, params = {}) {
    if (typeof value !== "string") {
        return value;
    }

    return value
        .replace(/\{\{(\w+)\}\}/g, (_, key) => {
            return params[key] === undefined ? "" : String(params[key]);
        })
        .replaceAll("&nbsp;", "\u00A0");
}

function getLanguageFromHost(hostname = "") {
    const normalizedHost = hostname.toLowerCase();

    if (normalizedHost === LANGUAGE_DOMAINS.pl || normalizedHost.endsWith(`.${LANGUAGE_DOMAINS.pl}`)) {
        return "pl";
    }

    return DEFAULT_LANGUAGE;
}

function getTargetUrl(language) {
    if (typeof window === "undefined") {
        return null;
    }

    const targetDomain = LANGUAGE_DOMAINS[language];
    const currentHost = window.location.hostname.toLowerCase();
    const isProductionDomain = Object.values(LANGUAGE_DOMAINS).some(
        (domain) => currentHost === domain || currentHost.endsWith(`.${domain}`)
    );

    if (!isProductionDomain || !targetDomain || currentHost === targetDomain) {
        return null;
    }

    return `${window.location.protocol}//${targetDomain}${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function I18nProvider({ children }) {
    const [language, setLanguageState] = useState(DEFAULT_LANGUAGE);

    useEffect(() => {
        const hostname = window.location.hostname;
        const domainLanguage = getLanguageFromHost(hostname);
        const isLocalHost = ["localhost", "127.0.0.1", "::1"].includes(hostname);
        const savedLanguage = window.localStorage.getItem(STORAGE_KEY);
        const initialLanguage =
            isLocalHost && SUPPORTED_LANGUAGES.includes(savedLanguage)
                ? savedLanguage
                : domainLanguage;

        setLanguageState(initialLanguage);
    }, []);

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.lang = translations[language].seo.language;
        }
    }, [language]);

    const setLanguage = useCallback((nextLanguage) => {
        if (!SUPPORTED_LANGUAGES.includes(nextLanguage)) {
            return;
        }

        window.localStorage.setItem(STORAGE_KEY, nextLanguage);
        const targetUrl = getTargetUrl(nextLanguage);

        if (targetUrl) {
            window.location.href = targetUrl;
            return;
        }

        setLanguageState(nextLanguage);
    }, []);

    const t = useCallback(
        (key, params) => {
            const localizedValue = getValueByPath(translations[language], key);
            const fallbackValue = getValueByPath(translations[DEFAULT_LANGUAGE], key);
            return interpolate(localizedValue ?? fallbackValue ?? key, params);
        },
        [language]
    );

    const translateTag = useCallback(
        (tag) => {
            const localizedTag = getValueByPath(translations[language], `projects.tags.${tag}`);
            const fallbackTag = getValueByPath(translations[DEFAULT_LANGUAGE], `projects.tags.${tag}`);

            return localizedTag ?? fallbackTag ?? tag;
        },
        [language]
    );

    const translateProject = useCallback(
        (project) => {
            if (!project) {
                return project;
            }

            const localizedProject = getValueByPath(translations[language], `projects.items.${project.slug}`);
            const fallbackProject = getValueByPath(translations[DEFAULT_LANGUAGE], `projects.items.${project.slug}`);

            return {
                ...project,
                ...(fallbackProject || {}),
                ...(localizedProject || {}),
            };
        },
        [language]
    );

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t,
            translateTag,
            translateProject,
        }),
        [language, setLanguage, t, translateTag, translateProject]
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}
